import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ButtonComponent } from "../../../../shared/button/button.component";
import { SharedModule } from "../../../../shared/shared.module";
import {
  BasicTrialNotificationTemplate
} from "../../../../core/models/trial-notification-template/basic-trial-notification-template.model";
import { TrialNotificationTemplateService } from "../../../../core/services/trial-notification-template.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { TrialNotificationFormComponent } from "./trial-notification-form/trial-notification-form.component";
import { ConfirmActionModalComponent } from "../../../../shared/confirm-action-modal/confirm-action-modal.component";
import {
  TrialNotificationCentreFormComponent,
} from "./trial-notification-centre-form/trial-notification-centre-form.component";
import { NotificationType } from "../../../../core/constants/notification-type.enum";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { NotificationTrigger } from "../../../../core/constants/notification-trigger.enum";
import { DiaryFormService } from "../../../../core/services/diary-form.service";
import { DiaryFormSummary } from "../../../../core/models/trial-diary-form/diary-form-summary.model";
import { SegmentControlComponent } from "../../../../shared/segment-control/segment-control.component";
import { SegmentStyle } from "../../../../shared/segment-control/segment-style.enum";

@Component({
  selector: 'app-trial-notifications',
  standalone: true,
  imports: [
    ButtonComponent,
    SharedModule,
    TrialNotificationFormComponent,
    TrialNotificationCentreFormComponent
  ],
  templateUrl: './trial-notifications.component.html',
  styleUrl: './trial-notifications.component.scss'
})
export class TrialNotificationsComponent implements OnInit {
  @ViewChild('segmentControl') segmentControl: SegmentControlComponent;
  @ViewChild('notificationFormModal') notificationFormModal: TrialNotificationFormComponent;
  @ViewChild('confirmModal') confirmModal: ConfirmActionModalComponent;

  @Input() trialId: string;
  @Input() diaryEnabled: boolean = false;

  private readonly _trialNotificationTemplateService = inject(TrialNotificationTemplateService);
  private readonly _alertService = inject(AlertService);
  private readonly _diaryFormService = inject(DiaryFormService);

  allNotifications: BasicTrialNotificationTemplate[] = [];
  ediaryNotifications: BasicTrialNotificationTemplate[] = [];
  visitNotifications: BasicTrialNotificationTemplate[] = [];
  diaryForms: DiaryFormSummary[] = [];
  selectedDiaryFormId: string | null = null;
  loading = false;
  loadingRowCount = 5;
  rowBusyIds: string[] = [];
  protected readonly SegmentStyle = SegmentStyle;
  protected currentSegment: number = 0;

  ngOnInit() {
    if (this.diaryEnabled) {
      this._diaryFormService.list(this.trialId).subscribe({
        next: diaries => {
          this.diaryForms = diaries.filter(d => d.enabled);
          if (this.diaryForms.length > 0) {
            this.selectedDiaryFormId = this.diaryForms[0].id;
          }
          this.loadNotificationTemplates();
        },
        error: () => this.loadNotificationTemplates()
      });
    } else {
      this.loadNotificationTemplates();
    }
  }

  onRemove(notification: BasicTrialNotificationTemplate) {
    this.confirmModal.showConfirmation(notification.id, 'Remove Notification', `Are you sure you want to remove '${notification.name}'?`);
  }

  frequencyLabel(notification: BasicTrialNotificationTemplate) {
    if (notification.notificationType === NotificationType.Ediary && notification.trigger === NotificationTrigger.Frequency)
      return notification.frequency;

    return '-';
  }

  onConfirmRemove(templateId: string) {
    this._trialNotificationTemplateService.remove(templateId).subscribe({
      next: notifications => {
        this.allNotifications = notifications;
        this.filterNotifications();
        this._alertService.showSuccessAlert('The notification has been removed successfully.');
      },
      error: error => {
        console.error(error);
        this._alertService.showErrorAlert(error);
      }
    });
  }

  loadNotificationTemplates() {
    this.loading = true;
    this._trialNotificationTemplateService.getAllForTrial(this.trialId).subscribe({
      next: notifications => {
        this.allNotifications = notifications;
        this.filterNotifications();
        this.loading = false;
      },
      error: error => {
        this._alertService.showErrorAlert(error);
        this.loading = false;
      }
    });
  }

  onNotificationTemplatesUpdated(templates: BasicTrialNotificationTemplate[]) {
    this.allNotifications = templates;
    this.filterNotifications();
  }

  selectDiaryForm(id: string) {
    this.selectedDiaryFormId = id;
    this.filterNotifications();
  }

  getDiaryDisplayName(diary: DiaryFormSummary): string {
    return diary.name?.trim() || 'Default Diary';
  }

  filterNotifications() {
    this.ediaryNotifications = this.allNotifications.filter(n =>
      n.notificationType === NotificationType.Ediary &&
      n.diaryFormId === this.selectedDiaryFormId
    );
    this.visitNotifications = this.allNotifications.filter(n =>
      n.notificationType === NotificationType.Visit
    );
  }

  segmentIndexChanged(index: number): void {
    this.currentSegment = index;
  }

  addEdiaryNotification(): void {
    this.notificationFormModal.add(NotificationType.Ediary, this.selectedDiaryFormId);
  }

  addVisitNotification(): void {
    this.notificationFormModal.add(NotificationType.Visit);
  }

  onToggleNotification(template: BasicTrialNotificationTemplate) {
    this.rowBusyIds.push(template.id);
    if (template.enabled) {
      this._trialNotificationTemplateService.disable(template.id).subscribe({next: rsp => {
        this.allNotifications.find(t => t.id == template.id).enabled = false;
        this.rowBusyIds = this.rowBusyIds.filter(id => id !== template.id);
      },
      error: error => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
        this.rowBusyIds = this.rowBusyIds.filter(id => id !== template.id);
      }})
    } else {
      this._trialNotificationTemplateService.enable(template.id).subscribe({next: rsp => {
          this.allNotifications.find(t => t.id == template.id).enabled = true;
          this.rowBusyIds = this.rowBusyIds.filter(id => id !== template.id);
        },
        error: error => {
          LogHelper.log(error);
          this._alertService.showErrorAlert(error);
          this.rowBusyIds = this.rowBusyIds.filter(id => id !== template.id);
        }})
    }
  }

  protected readonly NotificationType = NotificationType;
}
